import { Grid, TextInput, Select } from '@ts/storybook';
import { WrapperEntitySelector } from '@components/Inputs';
import JSONValidator from '@components/Inputs/JSONValidator';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CEntity, IEntity, IForm } from '@ts/ui-models';

const SYSTEMS = [
  { logicalId: 'imperial', name: 'Imperial' },
  { logicalId: 'metric', name: 'Metric' },
  { logicalId: 'any', name: 'Any' },
];

const CONVERSION_TYPES = [
  { logicalId: 'none', name: 'None' },
  { logicalId: 'product', name: 'Product' },
  { logicalId: 'sum', name: 'Sum' },
  { logicalId: 'special', name: 'Special' },
];

const specialQuantities = ['localCoordinate'];

const jsonExamples = {
  localCoordinate: {
    destinationProj: 'EPSG:4326',
    originCenterCoordinates: { latitude: 23.56, longitude: 76.5 },
    rotationAngle: 45,
  },
};

interface IUnitFormProps extends IForm {
  onChangeReadyToSubmit: (value: boolean) => void;
}

const UnitForm = (props: IUnitFormProps) => {
  const { mode, values, onChange, onCallbackChange, onChangeReadyToSubmit } = props;

  if (!values) return null;

  const { quantity, quantityId, name, ownerOrganization, system, abbreviation, conversionToSIBasic, conversionType, params } =
    values;

  const isViewMode = mode === 'view';

  const handleChangeEntitySelector = (entity: CEntity<IEntity>, entityName: string, fkField: string) => {
    const value = entity ? entity.getID() : null;
    onChange(entityName, entity);
    onChange(fkField, value);
  };

  const onChangeNumber = field => value => onChange(field, value);

  const onJSONValidated = newParamsJSON => {
    onChange('params', newParamsJSON);
    onChangeReadyToSubmit(true);
  };

  const renderConversionParams = () => {
    if (specialQuantities.includes(quantityId)) {
      return (
        <Grid size={12}>
          <JSONValidator
            label={'Conversion information parameters'}
            value={params}
            disabled={isViewMode}
            onJSONValidated={onJSONValidated}
            placeholder={jsonExamples[quantityId]}
          />
        </Grid>
      );
    } else {
      return (
        <>
          <Grid size={12}>
            <Select
              label="Conversion Type"
              value={conversionType || ''}
              onChange={onCallbackChange('conversionType')}
              options={CONVERSION_TYPES.map(v => ({ label: v.name, value: v.logicalId }))}
              isReadOnly={isViewMode}
            />
          </Grid>
          <Grid size={12}>
            <TextInput
              type="number"
              isReadOnly={isViewMode}
              label="Conversion to SI basic"
              value={conversionToSIBasic}
              onChangeValue={onChangeNumber('conversionToSIBasic')}
            />
          </Grid>
        </>
      );
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <TextInput isReadOnly={isViewMode} label="Name" value={name} onChangeValue={onCallbackChange('name')} />
      </Grid>
      <Grid size={12}>
        <TextInput
          isReadOnly={isViewMode}
          label="Abbreviation"
          value={abbreviation}
          onChangeValue={onCallbackChange('abbreviation')}
        />
      </Grid>
      <Grid size={12}>
        <Select
          label="System Type"
          value={system || ''}
          onChange={onCallbackChange('system')}
          options={SYSTEMS.map(v => ({ label: v.name, value: v.logicalId }))}
          isReadOnly={isViewMode}
        />
      </Grid>
      <Grid size={12}>
        <WrapperEntitySelector
          label={'Physical Quantity'}
          value={quantity?.id || quantityId}
          onChangeEntity={entity => handleChangeEntitySelector(entity as CEntity<IEntity>, 'quantity', 'quantityId')}
          searchComponent={generateSearchEntitySelector('physicalQuantity')}
          isReadOnly={isViewMode}
        />
      </Grid>
      {renderConversionParams()}
      <Grid size={12}>
        <WrapperEntitySelector
          label={'Organization'}
          value={ownerOrganization?.id}
          onChangeEntity={entity =>
            handleChangeEntitySelector(entity as CEntity<IEntity>, 'ownerOrganization', 'ownerOrganizationId')
          }
          searchComponent={generateSearchEntitySelector('organization')}
          isReadOnly={isViewMode}
        />
      </Grid>
    </Grid>
  );
};

export default UnitForm;
